import { useState, useEffect, createContext, useContext } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import api from './utils/api'
import Layout from './components/layout/Layout'
import Login from './pages/Login'
import DashboardList from './pages/DashboardList'
import DashboardView from './pages/DashboardView'
import DashboardDesigner from './pages/DashboardDesigner'
import DataSources from './pages/DataSources'
import QueryEditor from './pages/QueryEditor'
import Users from './pages/Users'
import Templates from './pages/Templates'
import Relations from './pages/Relations'
import Plasiyer from './pages/Plasiyer'
import AnlikRapor from './pages/AnlikRapor'
import AlberoOzet from './pages/AlberoOzet'
import Stok from './pages/Stok'
import GonderilmeyenFaturalar from './pages/GonderilmeyenFaturalar'
import AIAnaliz from './pages/AIAnaliz'
import AkilliArama from './pages/AkilliArama'
import CariRapor from './pages/CariRapor'
import AITahmin from './pages/AITahmin'
import SorguGecmisi from './pages/SorguGecmisi'
import AramaKayitlari from './pages/AramaKayitlari'
import ErrorBoundary from './components/ErrorBoundary'

const AuthContext = createContext(null)

export function useAuth() {
  return useContext(AuthContext)
}

function PrivateRoute({ children, adminOnly = false }) {
  const { user, token } = useAuth()
  if (!token) return <Navigate to="/login" replace />
  if (adminOnly && !user?.is_admin) return <Navigate to="/" replace />
  return children
}

// Ana sayfa: Albero özet özelliği olan (ve admin olmayan) kullanıcıyı doğrudan
// Anlık Rapor'a yönlendir; diğerleri dashboard listesini görür.
function HomeRoute() {
  const { user, companies } = useAuth()
  // Sadece kullanicinin KENDI firmasi (onceki oturumdan kalan liste baska firmaya atmasin)
  const feats = (companies || []).filter(c => c.id === user?.company_id)
    .reduce((a, c) => ({ ...a, ...(c.features || {}) }), {})
  if (!user?.is_admin && feats.albero_ozet) return <Navigate to="/albero-ozet" replace />
  return <DashboardList />
}

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem('token'))
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
  })
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    const saved = localStorage.getItem('selectedCompanyId')
    return saved ? (saved === 'all' ? null : parseInt(saved)) : null
  })
  const [companies, setCompanies] = useState([])

  const selectCompany = (id) => {
    setSelectedCompanyId(id)
    localStorage.setItem('selectedCompanyId', id === null ? 'all' : String(id))
  }

  // Token varsa kullanıcı bilgisini ve şirketleri sunucudan güncelle
  useEffect(() => {
    if (token) {
      api.get('/auth/me').then(({ data }) => {
        setUser(data)
        localStorage.setItem('user', JSON.stringify(data))
        // Server-side UI tercihlerini local'e yansit (theme, colorPalette, numberView)
        const prefs = data.preferences || {}
        if (prefs.theme) {
          localStorage.setItem('theme', prefs.theme)
          localStorage.setItem(`theme_user_${data.id}`, prefs.theme)
          if (prefs.theme === 'dark') document.documentElement.classList.add('dark')
          else document.documentElement.classList.remove('dark')
        }
        if (prefs.colorPalette) {
          localStorage.setItem('colorPalette', prefs.colorPalette)
          localStorage.setItem(`colorPalette_user_${data.id}`, prefs.colorPalette)
        }
        if (prefs.numberView) {
          localStorage.setItem('numberView', prefs.numberView)
          localStorage.setItem(`numberView_user_${data.id}`, prefs.numberView)
        }
        if (prefs.tableColSize) {
          localStorage.setItem('tableColSize', prefs.tableColSize)
          localStorage.setItem(`tableColSize_user_${data.id}`, prefs.tableColSize)
        }
        if (prefs.tableFontSize) {
          localStorage.setItem('tableFontSize', prefs.tableFontSize)
          localStorage.setItem(`tableFontSize_user_${data.id}`, prefs.tableFontSize)
        }
      }).catch(() => {
        logoutFn()
      })
      api.get('/users/companies').then(({ data }) => {
        setCompanies(data)
      }).catch(() => {})
    }
  }, [token])

  // UI tercihini sunucuya kaydet (kullanici bazli)
  const savePreference = (key, value) => {
    if (!token) return
    api.put('/auth/me/preferences', { [key]: value }).catch(() => {})
  }

  // Secili sirketin number_format'ini localStorage'a kaydet (ChartWidget icin)
  useEffect(() => {
    const company = companies.find(c => c.id === selectedCompanyId)
    const nf = company?.features?.number_format || {}
    localStorage.setItem('numberFormat', JSON.stringify(nf))
  }, [selectedCompanyId, companies])

  const login = (newToken, newUser) => {
    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
    setCompanies([])
    setToken(newToken)
    setUser(newUser)
  }

  const logoutFn = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('selectedCompanyId')
    setSelectedCompanyId(null)
    setCompanies([])
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ token, user, login, logout: logoutFn, selectedCompanyId, selectCompany, companies, savePreference }}>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={token ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<HomeRoute />} />
          <Route path="dashboard/:id" element={<DashboardView />} />
          <Route path="designer/:id" element={<PrivateRoute adminOnly><ErrorBoundary><DashboardDesigner /></ErrorBoundary></PrivateRoute>} />
          <Route path="datasources" element={<PrivateRoute adminOnly><DataSources /></PrivateRoute>} />
          <Route path="relations" element={<PrivateRoute adminOnly><Relations /></PrivateRoute>} />
          <Route path="queries" element={<PrivateRoute adminOnly><QueryEditor /></PrivateRoute>} />
          <Route path="users" element={<PrivateRoute adminOnly><Users /></PrivateRoute>} />
          <Route path="templates" element={<PrivateRoute adminOnly><Templates /></PrivateRoute>} />
          <Route path="plasiyer" element={<PrivateRoute><Plasiyer /></PrivateRoute>} />
          <Route path="albero-ozet" element={<PrivateRoute><AlberoOzet /></PrivateRoute>} />
          <Route path="stok" element={<PrivateRoute><Stok /></PrivateRoute>} />
          <Route path="gonderilmeyen-faturalar" element={<PrivateRoute><GonderilmeyenFaturalar /></PrivateRoute>} />
          <Route path="anlik-rapor" element={<PrivateRoute><AnlikRapor /></PrivateRoute>} />
          <Route path="ai-analiz" element={<PrivateRoute><AIAnaliz /></PrivateRoute>} />
          <Route path="akilli-arama" element={<PrivateRoute><AkilliArama /></PrivateRoute>} />
          <Route path="ai-tahmin" element={<PrivateRoute><AITahmin /></PrivateRoute>} />
          <Route path="cari-rapor" element={<PrivateRoute><CariRapor /></PrivateRoute>} />
          <Route path="arama-kayitlari" element={<PrivateRoute adminOnly={true}><AramaKayitlari /></PrivateRoute>} />
          <Route path="sorgu-gecmisi" element={<PrivateRoute adminOnly={true}><SorguGecmisi /></PrivateRoute>} />
        </Route>
      </Routes>
    </AuthContext.Provider>
  )
}
